import { isFunction } from './lodash';
// re-use canvas object for better performance
let canvas = null;
/**
 * 计算一段文本用指定字体渲染后的长度 (width)
 *
 * @category DOM
 * @param text - 文本
 * @param font - 字体, for example "bold 14px 'Helvetica Neue', 'Roboto', sans-serif"
 * @returns 渲染后的 width
 */
export function getTextWidth(text, font) {
    canvas = canvas !== null && canvas !== void 0 ? canvas : document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context)
        return 0;
    context.font = font;
    const metrics = context.measureText(text);
    return metrics.width;
}
/**
 * 将指定 html 元素转换为 canvas.
 * 内置了一些预处理逻辑:
 * 1. 目标元素内带有 data-html-to-canvas-remove 属性的元素都会在转换前被移除
 *
 * @category DOM
 * @param args - Argument object
 * @param args.el - 目标元素
 * @param args.scale - 放大倍数
 * @param args.preprocess - 转换前的预处理, 一般用于对样式进行调整 (在 clone 的元素中进行, 不影响原元素)
 * @param args.backgroundColor - 背景色
 * @returns Promise of canvas element
 */
export function htmlToCanvas({ el, preprocess, scale = 1, backgroundColor, }) {
    const originWidth = el.offsetWidth;
    // 为了在调整内容和样式时不影响原元素，需要 clone el
    let cloneEl = el.cloneNode(true);
    cloneEl.style.cssText += `;position: absolute;top: 0;left: 0;z-index: -999; width:${originWidth}px;`;
    if (preprocess)
        cloneEl = preprocess(cloneEl);
    // 删除带有属性 data-html-to-canvas-remove 的元素
    const delElement = cloneEl.querySelectorAll('[data-html-to-canvas-remove]');
    delElement.forEach(element => {
        var _a;
        (_a = element.parentNode) === null || _a === void 0 ? void 0 : _a.removeChild(element);
    });
    document.body.appendChild(cloneEl);
    return import('html-to-image').then(htmlToImage => new Promise(resolve => {
        setTimeout(() => {
            htmlToImage
                .toCanvas(cloneEl, { pixelRatio: scale, backgroundColor })
                .then(cvs => {
                resolve(cvs);
                setTimeout(() => {
                    document.body.removeChild(cloneEl);
                });
            });
            // 确保 el 渲染完成
        }, 400);
    }));
}
/**
 * 检查 ReactNode 是否有 props 属性
 *
 * @param jsx - ReactNode
 *
 * @returns boolean
 */
const hasProps = (jsx) => Object.prototype.hasOwnProperty.call(jsx, 'props');
/**
 *
 * 将 ReactNode 转为文本
 *
 * @param jsx - ReactNode
 *
 * @returns string
 */
export const getReactNodeInnerText = (jsx) => {
    // Empty
    if (jsx === null || typeof jsx === 'boolean' || typeof jsx === 'undefined') {
        return '';
    }
    // Numeric children.
    if (typeof jsx === 'number') {
        return jsx.toString();
    }
    // String literals.
    if (typeof jsx === 'string') {
        return jsx;
    }
    // Array of JSX.
    if (Array.isArray(jsx)) {
        return jsx.reduce((previous, current) => previous + getReactNodeInnerText(current), '');
    }
    // react element
    if (hasProps(jsx)) {
        if (Object.prototype.hasOwnProperty.call(jsx.props, 'children')) {
            return getReactNodeInnerText(jsx.props.children);
        }
        /*
         * 这更接近是一个经验的 Hack，有的 jsx 并不能下面的代码正常运行（遇到的其中一个情况是 jsx 中含有 hooks 而无法运行）
         * 那么在下面那行代码运行之前，尽可能的直接找出 jsx 中包含的文本。
         */
        if (Object.prototype.hasOwnProperty.call(jsx.props, 'text')) {
            return getReactNodeInnerText(jsx.props.text);
        }
        if (isFunction(jsx.type)) {
            try {
                return getReactNodeInnerText(jsx.type(jsx.props));
            }
            catch (e) {
                console.error('innerText 未能正确转译 ReactNode，请考虑将对应的渲染函数改成返回纯文本的形式。以下为具体错误：', e);
                return 'ReactNode 转译失败';
            }
        }
    }
    return '';
};
//# sourceMappingURL=data:application/json;base64,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